"use client";

import { useState } from "react";
import { SpecOptionPills } from "@/components/listing/FurnitureOptionalDimensionsFields";
import {
  PLAY_STATION_EDITIONS,
  PLAY_STATION_GENERATIONS,
  PLAY_STATION_GAMES_COUNT_MAX,
  PLAY_STATION_GAMES_COUNT_MIN,
  PLAY_STATION_PAD_COUNT_MAX,
  PLAY_STATION_PAD_COUNT_MIN,
  formatPlayStationEditionLabel,
  formatPlayStationGenerationLabel,
  type PlayStationEdition,
  type PlayStationGeneration,
  type PlayStationSpecFormState,
} from "@/lib/listing/playStationListingSpec";
import { cn } from "@/lib/utils";

interface PlayStationSpecFieldsProps {
  value: PlayStationSpecFormState;
  onChange: (next: PlayStationSpecFormState) => void;
  className?: string;
}

function countOptions(min: number, max: number): number[] {
  return Array.from({ length: max - min + 1 }, (_, i) => i + min);
}

/**
 * Step 2 panel for canonical `play-station`.
 * Generation required; edition + pad/game counts optional.
 */
export default function PlayStationSpecFields({
  value,
  onChange,
  className = "",
}: PlayStationSpecFieldsProps) {
  const hasExtras =
    (typeof value.padCount === "number" && Number.isFinite(value.padCount)) ||
    (typeof value.gamesCount === "number" && Number.isFinite(value.gamesCount));
  const [detailsOpen, setDetailsOpen] = useState(hasExtras);

  const parseOptionalSelect = (raw: string): number | "" => {
    if (!raw) return "";
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : "";
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Which PlayStation is it? <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...PLAY_STATION_GENERATIONS]}
          selected={value.playStationGeneration}
          onSelect={(playStationGeneration: PlayStationGeneration) =>
            onChange({ ...value, playStationGeneration })
          }
          formatLabel={formatPlayStationGenerationLabel}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Edition{" "}
          <span className="text-xs font-normal text-text-light">
            (optional)
          </span>
        </p>
        <SpecOptionPills
          options={[...PLAY_STATION_EDITIONS]}
          selected={value.playStationEdition}
          onSelect={(playStationEdition: PlayStationEdition) =>
            onChange({
              ...value,
              playStationEdition:
                value.playStationEdition === playStationEdition
                  ? ""
                  : playStationEdition,
            })
          }
          formatLabel={formatPlayStationEditionLabel}
        />
        <p className="text-xs text-text-light">
          Tap again to clear. Helps match similar comps (Slim / Pro).
        </p>
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setDetailsOpen((prev) => !prev)}
          className="flex w-full items-center justify-between rounded-lg border border-border bg-surface px-3 py-2 text-left text-sm font-medium text-foreground hover:border-border-strong"
          aria-expanded={detailsOpen}
        >
          <span>Pads &amp; games — optional</span>
          <span className="text-xs font-normal text-text-light">
            {detailsOpen ? "Hide" : "Add"}
          </span>
        </button>
        {detailsOpen ? (
          <div className="space-y-4 rounded-lg border border-border bg-secondary/40 p-3">
            <div className="space-y-2">
              <label
                htmlFor="play-station-pad-count"
                className="block text-sm font-medium text-foreground"
              >
                Number of pads
              </label>
              <select
                id="play-station-pad-count"
                value={value.padCount === "" ? "" : value.padCount}
                onChange={(e) =>
                  onChange({
                    ...value,
                    padCount: parseOptionalSelect(e.target.value),
                  })
                }
                className="w-full max-w-xs rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
              >
                <option value="">Skip</option>
                {countOptions(
                  PLAY_STATION_PAD_COUNT_MIN,
                  PLAY_STATION_PAD_COUNT_MAX,
                ).map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label
                htmlFor="play-station-games-count"
                className="block text-sm font-medium text-foreground"
              >
                Available games
              </label>
              <select
                id="play-station-games-count"
                value={value.gamesCount === "" ? "" : value.gamesCount}
                onChange={(e) =>
                  onChange({
                    ...value,
                    gamesCount: parseOptionalSelect(e.target.value),
                  })
                }
                className="w-full max-w-xs rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
              >
                <option value="">Skip</option>
                {countOptions(
                  PLAY_STATION_GAMES_COUNT_MIN,
                  PLAY_STATION_GAMES_COUNT_MAX,
                ).map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-xs text-text-light">
              Optional — skip if you don&apos;t know. Counts only (not game
              titles).
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
